import {
  addNewSubscriptionToNextOrder,
  addSubscriptionToNextOrder,
  cancelSubscription,
  getSubscriptions,
  reactivateSubscription,
  skipSubscription,
  updateChargeDate,
  updateSubscription,
  type Subscription,
} from '@/services/subscription'
import { defineStore } from 'pinia'
import { AsyncOperationState } from './lib/AsyncOperationState'
import { useNextOrderStore } from './nextOrder'
import { useProductImageStore } from './productImage'
import { useProductLinkStore } from './productLink'

import Bugsnag from '@bugsnag/browser'

export const useSubscriptionStore = defineStore('subscription', {
  state: () => ({
    loadState: AsyncOperationState.IDLE,
    workState: AsyncOperationState.IDLE,
    subscriptions: [] as Subscription[],
    stale: true as boolean,
  }),
  getters: {
    activeSubscriptions(state) {
      return state.subscriptions
        .filter((sub) => sub.status === 'active')
        .sort((sA, sB) => {
          if (!sA.next_charge_scheduled_at) return -1
          if (!sB.next_charge_scheduled_at) return 1
          return sA.next_charge_scheduled_at > sB.next_charge_scheduled_at
            ? 1
            : -1
        })
    },
    cancelledSubscriptions(state) {
      return state.subscriptions.reduce((subscriptions, sub) => {
        if (sub.status === 'cancelled') {
          const searchFn = (subscription: Subscription) =>
            subscription.external_product_id.ecommerce ===
            sub.external_product_id.ecommerce
          // dedupe cancelled subs based on product_id
          // TODO: maybe we should do it via variant id?
          if (
            !subscriptions.find(searchFn) &&
            !this.activeSubscriptions.find(searchFn)
          ) {
            subscriptions.push(sub)
          }
        }
        return subscriptions
      }, [] as Subscription[])
    },
  },
  actions: {
    async init(rechargeId: string) {
      if (this.stale || this.loadState === AsyncOperationState.IDLE) {
        await this.load(rechargeId)
      }
    },
    async load(rechargeId: string) {
      const productImageStore = useProductImageStore()
      const productLinkStore = useProductLinkStore()
      this.loadState = AsyncOperationState.IN_PROGRESS
      try {
        this.subscriptions = await getSubscriptions(rechargeId)
        productImageStore.loadImagesForProducts(
          this.subscriptions.map(
            (sub) =>
              `${sub.external_product_id.ecommerce}:${sub.external_variant_id.ecommerce}`,
          ),
        )
        productLinkStore.loadLinksForProducts(
          this.subscriptions.map(
            (sub) => `${sub.external_product_id.ecommerce}`,
          ),
        )
        this.stale = false
        this.loadState = AsyncOperationState.DONE
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.loadState = AsyncOperationState.ERRORED
      }
    },
    async patchSubscription(subscription: Subscription) {
      const productImageStore = useProductImageStore()
      const productLinkStore = useProductLinkStore()
      this.$patch(() => {
        const subscriptionIndex = this.subscriptions.findIndex(
          (s) => s.id === subscription.id,
        )
        this.subscriptions[subscriptionIndex] = subscription
      })
      await productImageStore.loadImagesForProducts([
        `${subscription.external_product_id.ecommerce}:${subscription.external_variant_id.ecommerce}`,
      ])
      await productLinkStore.loadLinksForProducts([
        `${subscription.external_product_id.ecommerce}`,
      ])
    },
    async updateSubscription(
      rechargeId: string,
      subscriptionId: number,
      update: {
        quantity: number
        frequency: number
        newVariantId?: string
      },
      addToNextOrder: boolean = false,
    ) {
      this.workState = AsyncOperationState.IN_PROGRESS
      const nextOrderStore = useNextOrderStore()

      try {
        let { subscription } = await updateSubscription(
          rechargeId,
          subscriptionId,
          update,
        )
        if (addToNextOrder) {
          ;({ subscription } = await addSubscriptionToNextOrder(
            rechargeId,
            subscriptionId,
          ))
        }
        await this.patchSubscription(subscription)
        this.workState = AsyncOperationState.DONE

        nextOrderStore.stale = true
        return subscription
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    async cancelSubscription(
      rechargeId: string,
      subscriptionId: number,
      reason: string,
      comments?: string,
    ) {
      this.workState = AsyncOperationState.IN_PROGRESS
      try {
        const { subscription } = await cancelSubscription(
          rechargeId,
          subscriptionId,
          reason,
          comments,
        )
        this.patchSubscription(subscription)
        this.workState = AsyncOperationState.DONE
        const nextOrderStore = useNextOrderStore()
        nextOrderStore.stale = true
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    async addSubscriptionToNextOrder(
      rechargeId: string,
      subscriptionId: number,
    ) {
      this.workState = AsyncOperationState.IN_PROGRESS
      try {
        const { subscription } = await addSubscriptionToNextOrder(
          rechargeId,
          subscriptionId,
        )
        this.patchSubscription(subscription)
        this.workState = AsyncOperationState.DONE
        const nextOrderStore = useNextOrderStore()
        nextOrderStore.stale = true
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    async addNewSubscriptionToNextOrder(
      rechargeId: string,
      productId: string,
      variantId: string,
      quantity: number,
      planId: string,
      offerPercent?: number,
    ) {
      this.workState = AsyncOperationState.IN_PROGRESS
      try {
        await addNewSubscriptionToNextOrder(
          rechargeId,
          productId,
          variantId,
          quantity,
          planId,
          offerPercent,
        )
        this.workState = AsyncOperationState.DONE
        const nextOrderStore = useNextOrderStore()
        nextOrderStore.stale = true
        this.stale = true
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    async skipSubscription(rechargeId: string, subscriptionId: number) {
      this.workState = AsyncOperationState.IN_PROGRESS

      try {
        const { subscription } = await skipSubscription(
          rechargeId,
          subscriptionId,
        )
        this.patchSubscription(subscription)
        this.workState = AsyncOperationState.DONE

        const nextOrderStore = useNextOrderStore()
        nextOrderStore.stale = true
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    async reactivateSubscription(
      rechargeId: string,
      subscriptionId: number,
      scheduledAt: string,
      quantity: number,
      frequency: number,
    ) {
      this.workState = AsyncOperationState.IN_PROGRESS

      try {
        const subscription = await reactivateSubscription(
          rechargeId,
          subscriptionId,
          scheduledAt,
          quantity,
          frequency,
        )
        this.patchSubscription(subscription)
        this.workState = AsyncOperationState.DONE

        const nextOrderStore = useNextOrderStore()
        nextOrderStore.stale = true
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    async updateChargeDate(
      rechargeId: string,
      subscriptionId: number,
      scheduledAt: string,
    ) {
      this.workState = AsyncOperationState.IN_PROGRESS

      try {
        const { subscription } = await updateChargeDate(
          rechargeId,
          subscriptionId,
          scheduledAt,
        )
        this.patchSubscription(subscription)
        this.workState = AsyncOperationState.DONE

        const nextOrderStore = useNextOrderStore()
        nextOrderStore.stale = true
      } catch (e) {
        Bugsnag.notify(e as Error)
        this.workState = AsyncOperationState.ERRORED
      }
    },
    resetWorkState() {
      this.workState = AsyncOperationState.IDLE
    },
  },
})
